import { createI18n } from 'vue-i18n'

const SUPPORTED_LOCALES = ['de', 'es', 'en', 'pt'] as const
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

function loadLocaleMessages(): Record<string, Record<string, unknown>> {
  const modules = import.meta.glob('../locale/*.json', { eager: true })
  const messages: Record<string, Record<string, unknown>> = {}

  for (const path in modules) {
    const match = /\/([\w-]+)\.json$/.exec(path)
    if (match?.[1]) {
      const mod = modules[path] as { default?: Record<string, unknown> } & Record<string, unknown>
      messages[match[1]] = mod.default ?? mod
    }
  }

  return messages
}

function detectLocale(): SupportedLocale {
  const localeRegex = /(^|;) ?LOCALE=([^;]*)(;|$)/
  const cookie = localeRegex.exec(document.cookie)

  if (cookie?.[2] && SUPPORTED_LOCALES.includes(cookie[2] as SupportedLocale)) {
    return cookie[2] as SupportedLocale
  }

  const browserLang = navigator.language?.split('-')[0]
  if (browserLang && SUPPORTED_LOCALES.includes(browserLang as SupportedLocale)) {
    return browserLang as SupportedLocale
  }

  const envLocale = import.meta.env.VITE_I18N_LOCALE
  if (envLocale && SUPPORTED_LOCALES.includes(envLocale as SupportedLocale)) {
    return envLocale as SupportedLocale
  }

  return 'en'
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: (import.meta.env.VITE_I18N_FALLBACK_LOCALE as SupportedLocale) || 'en',
  messages: loadLocaleMessages(),
  globalInjection: true,
  missingWarn: false,
  fallbackWarn: false,
})

export function setLocale(locale: SupportedLocale): void {
  i18n.global.locale.value = locale
  document.documentElement.setAttribute('lang', locale)
}

export { SUPPORTED_LOCALES }
export type { SupportedLocale }
export default i18n
